import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { materials } from "@/mocks/sustainability";

export default function MaterialsSection() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      gsap.from(".material-card", {
        y: 60,
        opacity: 0,
        duration: 1,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 70%" },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="bg-forest text-bone py-24 md:py-36 px-6 md:px-10 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(245,241,234,0.6) 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-14 md:mb-20 gap-6">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-clay mb-4">
              ✦ Our materials
            </p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tightest font-light leading-[0.95]">
              Every fiber, <span className="font-display text-clay">traced to its root.</span>
            </h2>
          </div>
          <p className="text-bone/70 max-w-sm text-sm md:text-base leading-relaxed">
            We use only four core fibers. Each one is chosen for its environmental performance, tactile quality, and the story it carries from field to atelier.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {materials.map((mat) => (
            <div key={mat.name} className="material-card group">
              <div className="relative aspect-[16/10] rounded-[24px] overflow-hidden bg-moss/20">
                <img
                  src={mat.image}
                  alt={mat.name}
                  className="w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-forest/80 backdrop-blur text-[10px] font-mono uppercase tracking-[0.2em] text-bone border border-bone/20">
                  {mat.origin}
                </div>
              </div>
              <div className="mt-6">
                <h3 className="font-display text-2xl md:text-3xl tracking-tightest font-light text-bone">
                  {mat.name}
                </h3>
                <p className="mt-3 text-bone/70 text-sm md:text-base leading-relaxed max-w-lg">
                  {mat.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}